// modules/social/SocialContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { socialService } from '../../core/services/firebase/socialService';
import { SocialProfile } from '../../core/types/social';

interface SocialContextType {
  profile: SocialProfile | null;
  loading: boolean;
  createProfile: (data: Partial<SocialProfile>) => Promise<void>;
  updateProfile: (updates: Partial<SocialProfile>) => Promise<void>;
  checkHandle: (handle: string) => Promise<boolean>;
  badgeAwarded: any | null;
  clearBadgeAwarded: () => void;
  awardBadge: (badge: any) => void;
}

const SocialContext = createContext<SocialContextType | undefined>(undefined);

export const SocialProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [profile, setProfile] = useState<SocialProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [badgeAwarded, setBadgeAwarded] = useState<any | null>(null);

  // Load Profile on Auth Change
  useEffect(() => {
    async function load() {
      if (user) {
        setLoading(true);
        try {
          const p = await socialService.getProfileByUserId(user.uid);
          setProfile(p);
        } catch (error) {
          console.error("Failed to load social profile", error);
        } finally {
          setLoading(false);
        }
      } else {
        setProfile(null);
        setLoading(false);
      }
    }
    load();
  }, [user]);

  const createProfile = async (data: Partial<SocialProfile>) => {
    if (!user) throw new Error("Must be logged in");
    
    // Default initial data
    const newProfile: any = {
      ...data,
      userId: user.uid,
      type: 'user',
      stats: { followers: 0, following: 0, posts: 0, reputation: 0 },
      settings: { isPublic: true, allowTagging: true, showContactInfo: false }
    };

    await socialService.createProfile(newProfile);
    
    // Refresh local state immediately
    const p = await socialService.getProfileByUserId(user.uid);
    setProfile(p);
  };

  const updateProfile = async (updates: Partial<SocialProfile>) => {
      if (!profile) throw new Error("No profile to update");
      await socialService.updateProfile(profile.handle, updates);
      
      // Optimistic or Fetch update
      setProfile(prev => prev ? { ...prev, ...updates } : null);
  };

  const checkHandle = async (handle: string) => {
    return socialService.isHandleAvailable(handle);
  };

  const awardBadge = (badge: any) => {
    setBadgeAwarded(badge);
  };

  const clearBadgeAwarded = () => {
    setBadgeAwarded(null);
  };

  return (
    <SocialContext.Provider value={{ 
      profile, 
      loading, 
      createProfile, 
      updateProfile, 
      checkHandle,
      badgeAwarded,
      clearBadgeAwarded,
      awardBadge
    }}>
      {children}
    </SocialContext.Provider>
  );
};

export const useSocial = () => {
  const context = useContext(SocialContext);
  if (context === undefined) {
    throw new Error('useSocial must be used within a SocialProvider');
  }
  return context;
};
